import { ReactNode, useEffect, useRef, useState } from 'react';

interface AgentDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** 'default' = ~512px (good for forms / config). 'wide' = roomier,
   *  for surfaces that need a tree + chat split (e.g. structure planner). */
  size?: 'default' | 'wide';
  children: ReactNode;
}

// Drag-resize lower margin (200px). Match Projects.tsx Discover
// slideout so all drawer-style modals behave consistently — keeps a
// strip of the underlying page visible behind the drawer.
const RESIZE_VIEWPORT_MARGIN = 200;
const RESIZE_MIN_WIDTH = 400;

export default function AgentDrawer({
  open,
  onClose,
  title,
  subtitle,
  size = 'default',
  children,
}: AgentDrawerProps) {
  // Drag-resize state. `paneWidth` stays null until the user actually
  // drags the handle — until then the drawer uses the responsive
  // Tailwind size classes (so default / wide layouts work without
  // any user action). Once the user drags, the inline style takes
  // precedence and the drawer stays at the user's chosen width.
  const [paneWidth, setPaneWidth] = useState<number | null>(null);
  const isResizing = useRef(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Drag-to-resize listeners. Mirrors the pattern in
  // Projects.tsx's Discover slideout so behaviour is identical.
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = window.innerWidth - e.clientX;
      setPaneWidth(
        Math.max(
          RESIZE_MIN_WIDTH,
          Math.min(newWidth, window.innerWidth - RESIZE_VIEWPORT_MARGIN),
        ),
      );
    };
    const onMouseUp = () => {
      isResizing.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Re-clamp when the viewport gets narrower (e.g. user resizes
  // their browser window after dragging the drawer wider than what
  // fits). Without this, a too-wide drawer would clip on the left.
  useEffect(() => {
    const onWindowResize = () => {
      setPaneWidth((current) => {
        if (current === null) return null;
        return Math.min(
          current,
          Math.max(RESIZE_MIN_WIDTH, window.innerWidth - RESIZE_VIEWPORT_MARGIN),
        );
      });
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
  };

  if (!open) return null;

  const defaultSizeClasses =
    size === 'wide'
      ? 'sm:w-[44rem] lg:w-[60rem] xl:w-[72rem]'
      : 'sm:w-[32rem]';

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer wrapper — flex so we can place the resize handle on the
          left edge alongside the drawer body. Inline `width` (set on
          drag) wins over the responsive size classes once the user
          customizes it. */}
      <div
        className={`absolute right-0 top-0 bottom-0 flex w-full ${
          paneWidth === null ? defaultSizeClasses : ''
        }`}
        style={paneWidth !== null ? { width: paneWidth } : undefined}
      >
        {/* Resize handle. Hidden on mobile where the drawer is
            full-width and dragging doesn't make sense. */}
        <div
          onMouseDown={handleResizeMouseDown}
          className="hidden sm:flex w-2 cursor-ew-resize bg-gray-200 dark:bg-gray-700 hover:bg-purple-400 active:bg-purple-500 transition-colors items-center justify-center group shrink-0"
          aria-hidden="true"
          title="Drag to resize"
        >
          <div className="w-1 h-8 bg-gray-400 group-hover:bg-purple-600 rounded-full" />
        </div>

        {/* Drawer body — `min-w-0` so children can shrink without
            forcing the drawer wider than the wrapper allows. */}
        <div
          className="flex-1 min-w-0 bg-white dark:bg-gray-800 shadow-2xl flex flex-col"
          role="dialog"
          aria-label={title}
        >
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}
